'use client';

import { forwardRef, useCallback, useContext, useId, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import { FormFieldContext } from '../form-field/context/form-field.context';
import type { FormFieldContextInterface } from '../form-field/types/form-field.types';
import { AdornedInputContext } from './context/adorned-input.context';
import { adornedInputRecipe } from './styles/adorned-input-root.styles';
import type {
  AdornedInputContextInterface,
  AdornedInputRootPropsInterface,
} from './types/adorned-input-root.types';

/**
 * @since 10.0.0
 */
export const AdornedInputRoot = forwardRef<HTMLDivElement, AdornedInputRootPropsInterface>(
  (props: AdornedInputRootPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);

    const defaultId = useId();

    const {
      children,
      className,
      css: cssProp,
      fluid = true,
      size = 'md',
      controlId = formFieldContext.controlId || defaultId,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      required = formFieldContext.required || false,
      readOnly = formFieldContext.readOnly || false,
      ...rest
    } = props;

    const adornments = useChildrenOfType(children, 'AdornedInput.Adornment');
    const input = useChildrenOfType(children, 'AdornedInput.Input');

    const defaultAdornmentIds = useMemo(() => {
      const inputIndex = Number.parseInt(input?.[0]?.key ?? '0', 10);
      const startAdornment = adornments.find(
        (adornment) => Number.parseInt(adornment.key ?? '0', 10) < inputIndex,
      );
      const endAdornment = adornments.find(
        (adornment) => Number.parseInt(adornment.key ?? '0', 10) > inputIndex,
      );

      const startAdornmentId = startAdornment?.props.id;
      const endAdornmentId = endAdornment?.props.id;

      return [startAdornmentId, endAdornmentId].filter(Boolean);
    }, [adornments, input]);

    const [adornmentIds, setAdornmentIds] = useState(new Set(defaultAdornmentIds));

    const setAdornmentId = useCallback(
      (id: string) => {
        setAdornmentIds((prev) => new Set([...prev, id]));
      },
      [setAdornmentIds],
    );

    const adornedInputContextValue = useMemo<AdornedInputContextInterface>(
      () => ({
        adornmentIds: Array.from(adornmentIds),
        setAdornmentId,
        size,
      }),
      [adornmentIds, setAdornmentId, size],
    );

    const formFieldContextValue = useMemo<FormFieldContextInterface>(
      () => ({
        ...formFieldContext,
        controlId,
        error,
        disabled,
        readOnly,
        required,
      }),
      [formFieldContext, controlId, error, disabled, readOnly, required],
    );

    const styles = css(adornedInputRecipe.raw({ size, fluid, disabled, error, readOnly }), cssProp);

    return (
      <FormFieldContext.Provider value={formFieldContextValue}>
        <AdornedInputContext.Provider value={adornedInputContextValue}>
          <div
            role="group"
            ref={forwardedRef}
            data-c11n-component="AdornedInput.Root"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', 'c11n_adorned-input__root', styles, className)}
            {...rest}
          >
            {children}
          </div>
        </AdornedInputContext.Provider>
      </FormFieldContext.Provider>
    );
  },
);

AdornedInputRoot.displayName = 'AdornedInput.Root';
